import replaceAllFromObject from '@/utils/strings/replaceAllFromObject';
import formatForURL from '@/utils/formatters/formatForURL';
import { logger } from '@/logger';
import { Request } from 'express';
import { getCanonicalOverride } from '@/server/repositories/canonicalOverrides';
import locationData from '@/server/constants/locationData';
import CATEGORIES_L1 from '@/constants/categories';

const LOCATION_FILLER_WORDS = new Set(['auto', 'autos', "auto's", 'in', 'uit']);

function extractLocationFromQuery(query: string): string | null {
  const normalizedQuery = query.toLowerCase().replace(/\+/g, ' ').trim();
  const words = normalizedQuery.split(/\s+/).filter(Boolean);
  const nonFillerWords = words.filter((word) => !LOCATION_FILLER_WORDS.has(word));

  if (nonFillerWords.length !== 1) {
    return null;
  }

  const potentialLocation = nonFillerWords[0];
  const locationKey = potentialLocation.replace(/\s+/g, '+');

  if (locationData[locationKey]) {
    return locationKey;
  }

  return null;
}

export default function getCanonicalUrl(req: Request, dominantCategory?: TDominantCategory): string {
  try {
    if (!req || !req.res || !req.res.locals) {
      throw new Error('getCanonicalUrl: req.res.locals is undefined');
    }

    const { searchQuery = '', fullRoute, l1Category = {}, requestData } = req.res.locals;
    const { hostname, params } = req;

    const rawPath = req.originalUrl ? req.originalUrl.split('?')[0] : req.path;
    const normalizedPath = rawPath && !rawPath.endsWith('/') ? `${rawPath}/` : rawPath;
    const tenantContext = requestData?.tenantContext;
    const tenantLocaleKey = tenantContext
      ? `${tenantContext.tenant}${tenantContext.locale.toLowerCase().replace('-', '')}`
      : '';
    const override = normalizedPath && getCanonicalOverride(tenantLocaleKey, normalizedPath);
    if (override) {
      return override;
    }

    const isMarktplaats = requestData?.tenantContext?.tenant === 'mp';
    const isCarsL1 = l1Category?.id === CATEGORIES_L1.CARS;

    if (isMarktplaats && isCarsL1 && searchQuery) {
      const locationKey = extractLocationFromQuery(searchQuery);
      if (locationKey) {
        return `https://${hostname}/l/${l1Category.key}/h/${locationKey}/`;
      }
    }

    // Dominant category experiment for l0 searches
    if (
      Object.keys(l1Category).length === 0 &&
      dominantCategory &&
      Object.keys(dominantCategory).length > 0 &&
      searchQuery !== ''
    ) {
      const parentKey = dominantCategory?.parentKey ? `/${dominantCategory.parentKey}` : '';
      return `https://${hostname}/l${parentKey}/${dominantCategory.key}/q/${formatForURL(searchQuery)}/`;
    }

    const replaceObjFromParams = Object.keys(params).reduce((acc, param) => {
      acc[`:${param}`] = params[param];
      return acc;
    }, {});

    const uriFromRoutePath = replaceAllFromObject(fullRoute, {
      ...replaceObjFromParams,
      ':query': formatForURL(searchQuery),
    });

    const url = `https://${hostname}${uriFromRoutePath}`;

    return url.substring(url.length - 1) === '/' ? url : `${url}/`;
  } catch (e) {
    logger.warn('seoTags error', e);
  }

  return '';
}
